import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../lib/api'
import {
  FEATURE_SURVEY_KEYS,
  FeatureSurveyAdminResponse,
  FeatureSurveyConfig,
  FeatureSurveyListItem,
  FeatureSurveyResults,
  FeatureSurveyStatus,
} from './types'

export function useFeatureSurveyStatus(enabled = true) {
  return useQuery<FeatureSurveyStatus>({
    queryKey: FEATURE_SURVEY_KEYS.status,
    queryFn: async () => (await api.get('/feature-survey/status')).data,
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })
}

export function useSubmitFeatureSurvey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      survey_key: string | null | undefined
      selected_options: string[]
      custom_answer?: string
    }) => api.post('/feature-survey/responses', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FEATURE_SURVEY_KEYS.status })
    },
  })
}

export function useAdminFeatureSurveyCurrent() {
  return useQuery<FeatureSurveyAdminResponse>({
    queryKey: FEATURE_SURVEY_KEYS.adminCurrent,
    queryFn: async () => (await api.get('/admin/feature-surveys/current')).data,
  })
}

export function useAdminFeatureSurveyList() {
  return useQuery<FeatureSurveyListItem[]>({
    queryKey: FEATURE_SURVEY_KEYS.adminList,
    queryFn: async () => (await api.get('/admin/feature-surveys')).data,
  })
}

export function useAdminFeatureSurveyResponses(surveyKey?: string) {
  return useQuery<FeatureSurveyResults>({
    queryKey: FEATURE_SURVEY_KEYS.adminResponses(surveyKey),
    queryFn: async () =>
      (
        await api.get('/admin/feature-surveys/responses', {
          params: surveyKey ? { survey_key: surveyKey } : undefined,
        })
      ).data,
  })
}

export function useUpdateAdminFeatureSurvey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: FeatureSurveyConfig) =>
      api.put('/admin/feature-surveys/current', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FEATURE_SURVEY_KEYS.adminCurrent })
      qc.invalidateQueries({ queryKey: FEATURE_SURVEY_KEYS.adminList })
      qc.invalidateQueries({ queryKey: ['adminFeatureSurveyResponses'] })
      qc.invalidateQueries({ queryKey: FEATURE_SURVEY_KEYS.status })
    },
  })
}
